import {
  IKnowledgeBaseClinicianBio,
  KNOWLEDGE_BASE_MAX_TEXT_LENGTH,
} from '@principle-theorem/principle-core/interfaces';
import { fromJSON } from '@principle-theorem/shared';
import { type OpenAI } from 'openai';

export const TEAM_OVERVIEW_SYSTEM_PROMPT =
  'You write concise, warm "our team" summaries for a dental practice\'s AI phone receptionist. ' +
  'Write in British (UK) English spelling and phrasing (e.g. "personalised", "specialising", "colour"). ' +
  'Given the practice name, clinician names, roles, and bios, write a single friendly paragraph of ' +
  '1 to 3 sentences that a receptionist could say to a caller, naturally mentioning the practice name. ' +
  'Do not use lists, headings, or markdown. Do not invent details that are not present in the bios. ' +
  'Keep it under 1000 characters.';

export const TEAM_OVERVIEW_SCHEMA: Record<string, unknown> = {
  type: 'object',
  properties: {
    overview: { type: 'string' },
  },
  required: ['overview'],
  additionalProperties: false,
};

export class TeamOverview {
  static buildMessages(
    clinicians: IKnowledgeBaseClinicianBio[],
    practiceName: string
  ): OpenAI.ChatCompletionMessageParam[] {
    const roster = clinicians
      .map((clinician) => {
        const role = clinician.specialty ? ` (${clinician.specialty})` : '';
        const bio = clinician.bio ? `: ${clinician.bio}` : '';
        return `- ${clinician.name}${role}${bio}`;
      })
      .join('\n');

    return [
      { role: 'system', content: TEAM_OVERVIEW_SYSTEM_PROMPT },
      {
        role: 'user',
        content:
          `Practice name: ${practiceName}\n\n` +
          `Write the "our team" summary from these clinicians:\n\n${roster}`,
      },
    ];
  }

  static parse(content: string): string {
    try {
      const parsed = fromJSON<{ overview?: unknown }>(content);
      const overview =
        typeof parsed.overview === 'string' ? parsed.overview : '';
      return overview.trim().slice(0, KNOWLEDGE_BASE_MAX_TEXT_LENGTH);
    } catch {
      return '';
    }
  }
}
